import React from 'react';
import { createMarkdownArticle } from '@uplift/pages';
import { content as technicalRequirements } from './content/technical-requirements';
import { content as cameraSafari } from './content/camera-safari';
import { content as cameraChrome } from './content/camera-chrome';
import { content as cameraEdge } from './content/camera-edge';
import { content as viewYourEarnedBadges } from './content/view-your-earned-badges';
import { content as taoGettingStarted } from './content/tao-getting-started';
import { content as signUpAndNavigationGuide } from './content/sign-up-and-navigation-guide';
import { content as whatServices } from './content/what-services';
import { WhatStatesArticle } from './what-states';
import { content as prescriptions } from './content/prescriptions';
import { content as doYouOfferInPersonAppointments } from './content/do-you-offer-in-person-appointments';
import { content as disabilityForms } from './content/disability-forms';
import { content as emotionalSupportAnimalLetter } from './content/emotional-support-animal-letter';
import { content as workplaceAccommodationsLetter } from './content/workplace-accommodations-letter';
import { content as courtOrderedTherapy } from './content/court-ordered-therapy';
import { content as verifyProviders } from './content/verify-providers';
import { content as substituteForInPersonTherapy } from './content/substitute-for-in-person-therapy';
import { content as howDoYouMatchMeWithATherapist } from './content/how-do-you-match-me-with-a-therapist';
import { content as howDoIStartTherapy } from './content/how-do-i-start-therapy';
import { content as howCanIContactMyProvider } from './content/how-can-i-contact-my-provider';
import { content as changeProvider } from './content/change-provider';
import { content as startPsychiatry } from './content/start-psychiatry';
import { content as scheduleSessions } from './content/schedule-sessions';
import { content as howDoIMeetWithMyProvider } from './content/how-do-i-meet-with-my-provider';
import { content as howSoonCanIStartAttendingTherapy } from './content/how-soon-can-i-start-attending-therapy';
import { content as travelingOrVacation } from './content/traveling-or-vacation';
import { content as doYouDoTherapySessionsForCouplesAndFamilies } from './content/do-you-do-therapy-sessions-for-couples-and-families';
import { content as doYouAcceptChildrenAsClients } from './content/do-you-accept-children-as-clients';
import { content as familyMembersShareAccount } from './content/family-members-share-account';
import { content as partnerProfileForCouples } from './content/partner-profile-for-couples';
import { content as couplesSeparateLocations } from './content/couples-separate-locations';
import { content as canIChangeMyPronounsWheneverIWant } from './content/can-i-change-my-pronouns-whenever-i-want';
import { content as howDoIUpdateMyAddress } from './content/how-do-i-update-my-address';
import { content as howDoIResetMyPassword } from './content/how-do-i-reset-my-password';
import { content as howDoIChangeMyPhoneNumber } from './content/how-do-i-change-my-phone-number';
import { content as howDoIChangeMyEmailAddress } from './content/how-do-i-change-my-email-address';
import { content as canISignUpForTextReminders } from './content/can-i-sign-up-for-text-reminders';
import { content as howDoIStopReceivingTextMessages } from './content/how-do-i-stop-receiving-text-messages';
import { content as howDoIChangeMyEmergencyContact } from './content/how-do-i-change-my-emergency-contact';
import { content as cardOnFileCharges } from './content/card-on-file-charges';
import { content as separateFee } from './content/separate-fee';
import { content as sessionCosts } from './content/session-costs';
import { content as paymentTypes } from './content/payment-types';
import { content as paymentSessionHistory } from './content/payment-session-history';
import { content as subscription } from './content/subscription';
import { AcceptMyInsuranceArticle } from './accept-my-insurance';
import { content as howDoIUpdateMyInsuranceInformation } from './content/how-do-i-update-my-insurance-information';
import { content as secondaryInsurance } from './content/secondary-insurance';
import { content as howDoIUpdateMyPaymentMethod } from './content/how-do-i-update-my-payment-method';
import { content as canIPayUsingMyHsaOrFsaCard } from './content/can-i-pay-using-my-hsa-or-fsa-card';
import { content as troubleJoiningSession } from './content/trouble-joining-session';
import { content as supportedDevices } from './content/supported-devices';
import { content as betterhelpRebrandFaq } from './content/betterhelp-rebrand-faq';
import {
  SUPPORT_ARTICLE_CATEGORY_DESCRIPTIONS,
  SUPPORT_ARTICLE_CATEGORY_LABELS,
  SUPPORT_ARTICLE_CATEGORY_ORDER,
  SUPPORT_ARTICLE_SLUGS,
  SupportArticleCategory,
  SupportArticleSlug,
} from './slugs';

export {
  SUPPORT_ARTICLE_CATEGORY_DESCRIPTIONS,
  SUPPORT_ARTICLE_CATEGORY_LABELS,
  SUPPORT_ARTICLE_CATEGORY_ORDER,
  SUPPORT_ARTICLE_SLUGS,
};
export type { SupportArticleCategory, SupportArticleSlug };

export type SupportArticleDefinition = {
  slug: SupportArticleSlug;
  title: string;
  category: SupportArticleCategory;
  Component: React.FC;
};

export const SUPPORT_ARTICLES: SupportArticleDefinition[] = [
  {
    slug: SUPPORT_ARTICLE_SLUGS.technicalRequirements,
    title: "What are the technical requirements to use BetterHelp?",
    category: 'technical',
    Component: createMarkdownArticle(technicalRequirements),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.cameraSafari,
    title: "Camera and Mic Permissions for Safari",
    category: 'technical',
    Component: createMarkdownArticle(cameraSafari),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.cameraChrome,
    title: "Camera and Mic Permissions for Chrome",
    category: 'technical',
    Component: createMarkdownArticle(cameraChrome),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.cameraEdge,
    title: "Camera and Mic Permissions for Microsoft Edge",
    category: 'technical',
    Component: createMarkdownArticle(cameraEdge),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.viewYourEarnedBadges,
    title: "View your earned badges",
    category: 'tao',
    Component: createMarkdownArticle(viewYourEarnedBadges),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.taoGettingStarted,
    title: "A guide to get started with TAO by BetterHelp",
    category: 'tao',
    Component: createMarkdownArticle(taoGettingStarted),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.signUpAndNavigationGuide,
    title: "Sign-up and Navigation Guide",
    category: 'tao',
    Component: createMarkdownArticle(signUpAndNavigationGuide),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.whatServices,
    title: "What services does BetterHelp offer?",
    category: 'getting-started',
    Component: createMarkdownArticle(whatServices),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.whatStates,
    title: "What states does BetterHelp work in?",
    category: 'services',
    Component: WhatStatesArticle,
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.prescriptions,
    title: "Can I get a prescription for medication on BetterHelp?",
    category: 'services',
    Component: createMarkdownArticle(prescriptions),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.doYouOfferInPersonAppointments,
    title: "Do you offer in-person appointments?",
    category: 'services',
    Component: createMarkdownArticle(doYouOfferInPersonAppointments),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.disabilityForms,
    title: "Do your therapists or psychiatrists complete disability forms?",
    category: 'services',
    Component: createMarkdownArticle(disabilityForms),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.emotionalSupportAnimalLetter,
    title: "Can my therapist write me a letter for an emotional support animal?",
    category: 'services',
    Component: createMarkdownArticle(emotionalSupportAnimalLetter),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.workplaceAccommodationsLetter,
    title: "Can BetterHelp write me a letter for educational and/or workplace accommodations?",
    category: 'services',
    Component: createMarkdownArticle(workplaceAccommodationsLetter),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.courtOrderedTherapy,
    title: "Do your therapists and psychiatrists offer court-ordered therapy or evaluations?",
    category: 'services',
    Component: createMarkdownArticle(courtOrderedTherapy),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.verifyProviders,
    title: "How do you verify and approve your therapists and psychiatric providers?",
    category: 'services',
    Component: createMarkdownArticle(verifyProviders),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.substituteForInPersonTherapy,
    title: "Can BetterHelp substitute for traditional face-to-face therapy?",
    category: 'services',
    Component: createMarkdownArticle(substituteForInPersonTherapy),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoYouMatchMeWithATherapist,
    title: "How do you match me with a therapist?",
    category: 'matching',
    Component: createMarkdownArticle(howDoYouMatchMeWithATherapist),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIStartTherapy,
    title: "How do I start therapy?",
    category: 'matching',
    Component: createMarkdownArticle(howDoIStartTherapy),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howCanIContactMyProvider,
    title: "How can I contact my provider?",
    category: 'matching',
    Component: createMarkdownArticle(howCanIContactMyProvider),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.changeProvider,
    title: "How do I change my provider?",
    category: 'matching',
    Component: createMarkdownArticle(changeProvider),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.startPsychiatry,
    title: "How do I start psychiatry or medication management?",
    category: 'matching',
    Component: createMarkdownArticle(startPsychiatry),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.scheduleSessions,
    title: "How do I schedule BetterHelp sessions?",
    category: 'sessions',
    Component: createMarkdownArticle(scheduleSessions),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIMeetWithMyProvider,
    title: "How do I meet with my provider?",
    category: 'sessions',
    Component: createMarkdownArticle(howDoIMeetWithMyProvider),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howSoonCanIStartAttendingTherapy,
    title: "How soon can I start attending therapy?",
    category: 'getting-started',
    Component: createMarkdownArticle(howSoonCanIStartAttendingTherapy),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.travelingOrVacation,
    title: "Can I still see my therapist if I am traveling or on vacation?",
    category: 'sessions',
    Component: createMarkdownArticle(travelingOrVacation),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.doYouDoTherapySessionsForCouplesAndFamilies,
    title: "Do you do therapy sessions for couples and families?",
    category: 'family-couples',
    Component: createMarkdownArticle(doYouDoTherapySessionsForCouplesAndFamilies),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.doYouAcceptChildrenAsClients,
    title: "Do you accept children as clients?",
    category: 'family-couples',
    Component: createMarkdownArticle(doYouAcceptChildrenAsClients),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.familyMembersShareAccount,
    title: "Can my family members share one BetterHelp account?",
    category: 'family-couples',
    Component: createMarkdownArticle(familyMembersShareAccount),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.partnerProfileForCouples,
    title: "I want my partner to join me for a couples therapy session. Do they need to make their own profile?",
    category: 'family-couples',
    Component: createMarkdownArticle(partnerProfileForCouples),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.couplesSeparateLocations,
    title: "For couples and family therapy, can we join sessions from separate locations?",
    category: 'family-couples',
    Component: createMarkdownArticle(couplesSeparateLocations),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.canIChangeMyPronounsWheneverIWant,
    title: "Can I change my pronouns whenever I want?",
    category: 'account',
    Component: createMarkdownArticle(canIChangeMyPronounsWheneverIWant),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIUpdateMyAddress,
    title: "How do I update my address?",
    category: 'account',
    Component: createMarkdownArticle(howDoIUpdateMyAddress),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIResetMyPassword,
    title: "How do I reset my password?",
    category: 'account',
    Component: createMarkdownArticle(howDoIResetMyPassword),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIChangeMyPhoneNumber,
    title: "How do I change my phone number?",
    category: 'account',
    Component: createMarkdownArticle(howDoIChangeMyPhoneNumber),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIChangeMyEmailAddress,
    title: "How do I change my email address?",
    category: 'account',
    Component: createMarkdownArticle(howDoIChangeMyEmailAddress),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.canISignUpForTextReminders,
    title: "Can I sign up for text reminders?",
    category: 'account',
    Component: createMarkdownArticle(canISignUpForTextReminders),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIStopReceivingTextMessages,
    title: "How do I stop receiving text messages?",
    category: 'account',
    Component: createMarkdownArticle(howDoIStopReceivingTextMessages),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIChangeMyEmergencyContact,
    title: "How do I change my emergency contact?",
    category: 'account',
    Component: createMarkdownArticle(howDoIChangeMyEmergencyContact),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.cardOnFileCharges,
    title: "When will my card on file be charged?",
    category: 'payments',
    Component: createMarkdownArticle(cardOnFileCharges),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.separateFee,
    title: "Is there a separate fee to use BetterHelp?",
    category: 'payments',
    Component: createMarkdownArticle(separateFee),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.sessionCosts,
    title: "How much will I pay for sessions?",
    category: 'payments',
    Component: createMarkdownArticle(sessionCosts),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.paymentTypes,
    title: "What types of payment do BetterHelp providers accept?",
    category: 'payments',
    Component: createMarkdownArticle(paymentTypes),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.paymentSessionHistory,
    title: "How do I view my payment or session history?",
    category: 'payments',
    Component: createMarkdownArticle(paymentSessionHistory),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.subscription,
    title: "Do I need a subscription to use BetterHelp?",
    category: 'payments',
    Component: createMarkdownArticle(subscription),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.acceptMyInsurance,
    title: "Does BetterHelp accept my insurance?",
    category: 'payments',
    Component: AcceptMyInsuranceArticle,
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIUpdateMyInsuranceInformation,
    title: "How do I update my insurance information?",
    category: 'payments',
    Component: createMarkdownArticle(howDoIUpdateMyInsuranceInformation),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.secondaryInsurance,
    title: "How do I add a secondary insurance, like Medicare?",
    category: 'payments',
    Component: createMarkdownArticle(secondaryInsurance),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.howDoIUpdateMyPaymentMethod,
    title: "How do I update my payment method?",
    category: 'payments',
    Component: createMarkdownArticle(howDoIUpdateMyPaymentMethod),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.canIPayUsingMyHsaOrFsaCard,
    title: "Can I pay using my HSA or FSA card?",
    category: 'payments',
    Component: createMarkdownArticle(canIPayUsingMyHsaOrFsaCard),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.troubleJoiningSession,
    title: "I'm having trouble joining my session. What should I do?",
    category: 'technical',
    Component: createMarkdownArticle(troubleJoiningSession),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.supportedDevices,
    title: "Which devices can I use for BetterHelp?",
    category: 'technical',
    Component: createMarkdownArticle(supportedDevices),
  },
  {
    slug: SUPPORT_ARTICLE_SLUGS.betterhelpRebrandFaq,
    title: "BetterHelp Rebrand FAQ",
    category: 'general',
    Component: createMarkdownArticle(betterhelpRebrandFaq),
  },
];

export const getSupportArticleBySlug = (slug?: string) =>
  SUPPORT_ARTICLES.find(article => article.slug === slug);

export const isSupportArticleCategory = (value?: string): value is SupportArticleCategory =>
  !!value && SUPPORT_ARTICLE_CATEGORY_ORDER.includes(value as SupportArticleCategory);

export const getSupportCategory = (category?: string) => {
  if (!isSupportArticleCategory(category)) {
    return undefined;
  }

  return {
    category,
    label: SUPPORT_ARTICLE_CATEGORY_LABELS[category],
    description: SUPPORT_ARTICLE_CATEGORY_DESCRIPTIONS[category],
    articles: SUPPORT_ARTICLES.filter(article => article.category === category),
  };
};

export const getSupportArticlesByCategory = () =>
  SUPPORT_ARTICLE_CATEGORY_ORDER.map(category => ({
    category,
    label: SUPPORT_ARTICLE_CATEGORY_LABELS[category],
    description: SUPPORT_ARTICLE_CATEGORY_DESCRIPTIONS[category],
    articles: SUPPORT_ARTICLES.filter(article => article.category === category),
  }));
